/**
 * Vowed iPhone Gate PWA — signing core (D-046).
 *
 * The browser side of the Credential Broker gate. It generates the gate's Ed25519 keypair
 * (WebCrypto), and signs the broker Verdict over the SAME canonical CBOR tuple the daemon
 * verifies (`src/daemon/broker/verdict.ts:verdictSigningBytes`). Because both sides use
 * cbor-x with the identical field order, the bytes are identical and the daemon's libsodium
 * verify accepts the WebCrypto signature (proven in verdict-webcrypto-interop.test.ts).
 *
 * This module is browser code (DOM/WebCrypto types) but runs unchanged in Node (which has
 * global crypto.subtle + btoa), so it is unit-testable against the real daemon verifier.
 */

import { encode as cborEncode } from "cbor-x";

export interface VerdictTarget {
  daemon_id: string;
  vault_id: string;
}

export interface SignedVerdictFields {
  request_id: string;
  decision: "approve" | "deny";
  approved_descriptor_hash: string;
  code: string;
  nonce: string;
  gate_device_id: string;
  target: VerdictTarget;
  expiry: number;
  /**
   * SPEC A2.7 (anti-confused-deputy). MUST mirror the daemon's verdict.ts:SignedVerdictFields.op.
   * "unlock" for a phone-gated-unlock verdict; ABSENT for legacy credential grants. Folded into
   * the CBOR map ONLY when present (see verdictSigningBytes) — so a grant verdict signs
   * BYTE-IDENTICALLY to before, while an unlock verdict commits to op. Without this field the
   * PWA-signed unlock verdict's bytes differed from the daemon's and verification ALWAYS failed
   * (SEC-PGU-001): the unlock path was unfireable end-to-end with the deployed gate.
   */
  op?: string;
}

/** MUST byte-match the daemon's verdict.ts:verdictSigningBytes (same cbor-x, same field order).
 *
 * `op` (SPEC A2.7) is folded in ONLY when present — IDENTICAL to the daemon's construction — so a
 * grant verdict (op absent) produces byte-identical output to the pre-leg-7 tuple (grant signing
 * unchanged), while an unlock verdict (op="unlock") commits to op. */
export function verdictSigningBytes(f: SignedVerdictFields): Uint8Array {
  const tuple: Record<string, unknown> = {
    request_id: f.request_id,
    decision: f.decision,
    approved_descriptor_hash: f.approved_descriptor_hash,
    code: f.code,
    nonce: f.nonce,
    gate_device_id: f.gate_device_id,
    target: { daemon_id: f.target.daemon_id, vault_id: f.target.vault_id },
    expiry: f.expiry,
  };
  if (f.op !== undefined) tuple.op = f.op;
  return cborEncode(tuple);
}

function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export interface GateKeypair {
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  /** base64 raw 32-byte Ed25519 public key — what gets enrolled. */
  pubkeyB64: string;
}

/**
 * Generate the gate's Ed25519 keypair. The private key is NON-EXTRACTABLE (false): it can
 * sign but its raw bytes never leave the browser key store — it is stored in IndexedDB BY
 * REFERENCE (CryptoKey is structured-cloneable regardless of extractability). This matches
 * the app's "non-extractable" contract (previously the flag was `true`, contradicting it).
 * The public key is always extractable, independent of the private-key flag, so exportKey
 * below still yields the raw 32-byte pubkey that gets enrolled.
 */
export async function generateGateKeypair(): Promise<GateKeypair> {
  const kp = (await crypto.subtle.generateKey({ name: "Ed25519" }, false, [
    "sign",
    "verify",
  ])) as CryptoKeyPair;
  const raw = new Uint8Array(await crypto.subtle.exportKey("raw", kp.publicKey));
  return { privateKey: kp.privateKey, publicKey: kp.publicKey, pubkeyB64: toBase64(raw) };
}

/** Sign a broker verdict (after the operator confirmed the code + Face ID). */
export async function signVerdict(
  fields: SignedVerdictFields,
  privateKey: CryptoKey,
  pubkeyB64: string,
): Promise<SignedVerdictFields & { sig: string; signer_pubkey: string }> {
  const sig = new Uint8Array(
    await crypto.subtle.sign({ name: "Ed25519" }, privateKey, verdictSigningBytes(fields)),
  );
  return { ...fields, sig: toBase64(sig), signer_pubkey: pubkeyB64 };
}

/** Sign the enrollment nonce to prove key possession (gate_enroll_complete). */
export async function signEnrollNonce(nonce: string, privateKey: CryptoKey): Promise<string> {
  const sig = new Uint8Array(
    await crypto.subtle.sign({ name: "Ed25519" }, privateKey, new TextEncoder().encode(nonce)),
  );
  return toBase64(sig);
}

function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

/**
 * Compute the out-of-band safety number — MUST byte-match the daemon's
 * `src/sync/pairing.ts:computeSafetyNumber` (sort the two pubkeys, SHA-256, 6 groups of 5
 * digits). The operator confirms the PWA's number matches the Mac's before enrolling.
 */
export async function computeSafetyNumber(pubA: Uint8Array, pubB: Uint8Array): Promise<string> {
  const [a, b] = compareBytes(pubA, pubB) <= 0 ? [pubA, pubB] : [pubB, pubA];
  const concat = new Uint8Array(a.length + b.length);
  concat.set(a);
  concat.set(b, a.length);
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", concat)).subarray(0, 30);
  const groups: string[] = [];
  for (let i = 0; i < 30; i += 5) {
    let num = 0;
    for (let j = i; j < Math.min(i + 5, 30); j++) num = (num * 256 + digest[j]) % 100000;
    groups.push(String(num).padStart(5, "0"));
  }
  return groups.join(" ");
}

export function base64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/* ─────────────────────── Admin op `mint_service_token` (D-028a) ───────────────────────
 * The daemon sends NO precomputed hash for a service-token mint. The phone RECOMPUTES the canonical
 * descriptor hash from exactly the fields it renders and signs that (sign-what-you-see). A relay or
 * CLI that altered any field produces a hash the daemon rejects; an OLD gate build (which signs the
 * delivered hash) signs nothing and fails closed. MUST byte-match the daemon's
 * `src/daemon/broker/descriptor.ts` freezeDescriptor (proven by the shared golden-vector test). */

/** The `op` a service-token mint verdict MUST carry (I10). */
export const MINT_SERVICE_TOKEN_OP = "mint_service_token";

/** The frozen mint descriptor — every field the phone shows, and nothing else. */
export interface MintDescriptorFields {
  op: string;
  requestor_session: string;
  wallets: string[];
  ops: string[];
  names: string[];
  tags: string[];
  window: { ttl_s: number };
  requested_at: number;
  service_id: string;
  reason: string;
  machine: string;
  target: VerdictTarget;
}

/** MUST mirror the daemon's descriptor.ts canonicalize(): keys sorted recursively, scalar arrays sorted. */
export function canonicalizeDescriptor(value: unknown): unknown {
  if (Array.isArray(value)) {
    const mapped = value.map(canonicalizeDescriptor);
    if (mapped.every((v) => typeof v === "string" || typeof v === "number")) return [...mapped].sort();
    return mapped;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      out[key] = canonicalizeDescriptor((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

/** SHA-256 hex over the canonical CBOR of the mint descriptor, built field-by-field (no extras). */
export async function mintDescriptorHash(d: MintDescriptorFields): Promise<string> {
  const explicit = {
    op: d.op,
    requestor_session: d.requestor_session,
    wallets: [...d.wallets],
    ops: [...d.ops],
    names: [...d.names],
    tags: [...d.tags],
    window: { ttl_s: d.window.ttl_s },
    requested_at: d.requested_at,
    service_id: d.service_id,
    reason: d.reason,
    machine: d.machine,
    target: { daemon_id: d.target.daemon_id, vault_id: d.target.vault_id },
  };
  const bytes = new Uint8Array(cborEncode(canonicalizeDescriptor(explicit)));
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  let hex = "";
  for (const b of digest) hex += b.toString(16).padStart(2, "0");
  return hex;
}

/* ───────────────────────── Enrollment attestation (R1 companion, D-073) ─────────────────────────
 * The SUBSEQUENT-enrollment path: an ALREADY-ENROLLED phone signs an `enroll_attest` verdict over a
 * new gate's identity so the daemon's fail-closed `gate_enroll_complete` accepts it (clears E834).
 * These two pure functions MUST byte-match the daemon's `src/daemon/broker/enroll-attestation.ts`
 * (proven in tests/gate-pwa/enroll-attest-sign.test.ts) so the phone can RECOMPUTE both values from
 * the request itself — signing what it displays, never trusting a relay-supplied hash/fingerprint. */

/** The `op` an enrollment-attestation verdict MUST carry (anti-confused-deputy; SPEC A2.7). */
export const ENROLL_ATTEST_OP = "enroll_attest";

/**
 * WebCrypto mirror of the daemon's `enrollAttestationHash` — SHA-256 over
 * "vowed-enroll-attest:v1"\0<gateId>\0<pubkeyB64>\0<nonce> (UTF-8), hex. Byte-identical to the
 * daemon so the signed `approved_descriptor_hash` matches what gate_enroll_complete recomputes.
 */
export async function enrollAttestationHash(gateId: string, pubkeyB64: string, nonce: string): Promise<string> {
  const enc = new TextEncoder();
  const nul = new Uint8Array([0]);
  const parts = [enc.encode("vowed-enroll-attest:v1"), nul, enc.encode(gateId), nul, enc.encode(pubkeyB64), nul, enc.encode(nonce)];
  const total = parts.reduce((n, p) => n + p.length, 0);
  const buf = new Uint8Array(total);
  let o = 0;
  for (const p of parts) { buf.set(p, o); o += p.length; }
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", buf));
  let hex = "";
  for (const b of digest) hex += b.toString(16).padStart(2, "0");
  return hex;
}

/**
 * WebCrypto mirror of the daemon's `enrollFingerprint` — a deterministic human-verifiable number
 * over the new gate's pubkey ALONE (SHA-256 → 6 groups of 5 digits, same grouping as
 * computeSafetyNumber). The operator compares it against the number the NEW phone shows.
 */
export async function enrollFingerprint(pubkeyB64: string): Promise<string> {
  // Copy into a fresh ArrayBuffer-backed view so digest()'s BufferSource type is satisfied
  // (base64ToBytes' Uint8Array is ArrayBufferLike-typed under strict lib.dom).
  const raw = new Uint8Array(base64ToBytes(pubkeyB64));
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", raw)).subarray(0, 30);
  const groups: string[] = [];
  for (let i = 0; i < 30; i += 5) {
    let num = 0;
    for (let j = i; j < i + 5; j++) num = (num * 256 + digest[j]) % 100000;
    groups.push(String(num).padStart(5, "0"));
  }
  return groups.join(" ");
}
